import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMPurify from 'dompurify';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import truncateText from 'views_shared/app/javascript/utils/truncateText';
import { isPitchOrigin } from 'views_shared/app/javascript/constants/conversationOriginTypes';
import ConfirmationModal from 'views_shared/app/javascript/components/ConfirmationModal';

import ConversationProfileImages from './ConversationProfileImages';
import ConversationDropdown from '../../ConversationDropdown';
import formatLastSentAt from './formatLastSentAt';
import stripHtmlTags from './stripHtmlTags';

function Conversation({
  conversation,
  handleClick,
  selectedConversation,
  onActionComplete,
  selectionMode = false,
  isSelected = false,
  onToggleSelection,
  activeMailbox = null,
  // Search results mix tabs, so a trashed row must say so even when searching FROM Trash
  inSearchResults = false,
  // Extra badges beside the name (the request-page rail marks fast/flagged pitches here)
  titleBadges = null,
  dropdownOverride = null,
}) {
  const currentReceiverSlug = globalThis.Qwoted.CURRENT_SLUG;
  const currentReceiverProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // Identify the viewer by stable profile id; slug is a fallback for id-less rows.
  const isViewer = (p) => (currentReceiverProfileId != null && p.id === currentReceiverProfileId)
    || p.slug === currentReceiverSlug;

  const dedupeBySlug = (profiles) => {
    const seen = new Set();
    return profiles.filter((p) => {
      if (seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    });
  };

  const allParticipants = dedupeBySlug([...conversation.conversation_senders, ...conversation.conversation_receivers]);
  const otherParticipants = allParticipants.filter((p) => !isViewer(p));

  // Fallback to all participants if current user is the only one
  const participants = otherParticipants.length > 0 ? otherParticipants : allParticipants;

  // On message-source threads the participant is the PR rep. Pick who reads first based on the
  // viewer's role: expert viewing → media first; PR or media viewing → represented expert first.
  // "Represented entity" is either an Expert or a Product — same JSONB shape on both ends.
  const rawOriginExpert = conversation.origin_expert;
  const rawOriginProduct = conversation.origin_product;
  // The viewer is "the expert" only when they are the self-represented profile behind this thread.
  // Key on the stable profile id, not the mutable slug; the null guard avoids undefined===undefined.
  const viewerIsExpert = currentReceiverProfileId != null
    && rawOriginExpert?.self_represented_profile_id === currentReceiverProfileId;
  // Products can't be viewers, so the expert-viewer short-circuit only applies on expert origins.
  const originEntity = viewerIsExpert ? null : rawOriginExpert || rawOriginProduct;
  const isMediaProfile = (p) => p.type_of_profile === 'type_media';

  let displayProfiles;
  if (viewerIsExpert) {
    displayProfiles = [
      ...otherParticipants.filter(isMediaProfile),
      ...otherParticipants.filter((p) => !isMediaProfile(p)),
    ];
  } else if (originEntity) {
    // With an origin entity we don't need the current-user fallback — the entity covers it.
    displayProfiles = dedupeBySlug([originEntity, ...otherParticipants]);
  } else {
    displayProfiles = participants;
  }

  // Orphaned/self-only threads can leave this empty; fall back so the row renders instead of crashing the inbox.
  if (displayProfiles.length === 0) {
    displayProfiles = allParticipants;
  }

  const firstNameOf = (p) => p.first_name || p.name?.split(' ')[0] || p.name || '';
  const displayName = displayProfiles.length > 1 ? displayProfiles.map(firstNameOf).join(', ') : displayProfiles[0]?.name || '';

  // Subtitle only when a single person is rendered; multi-name rows stay tight.
  const displayTitle = displayProfiles.length === 1 ? displayProfiles[0]?.title || displayProfiles[0]?.title_at_workplace || '' : '';

  const selected = conversation.conversation_id === selectedConversation?.conversation_id;
  const isTrashed = Boolean(conversation.receipt_archived_at);
  const isMuted = Boolean(conversation.muted_at ?? conversation.receipt_muted_at);

  const unread = !conversation.receipt_read_at;

  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRestore = async (e) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      const result = await qapi.conversations.restoreById(conversation.conversation_id);
      if (result.error) throw new Error(result.error.statusText || 'Failed to restore');
      globalThis.Qwoted.showToast({ toastTitle: 'Restored', toastBody: 'Conversation restored', variant: 'success' });
      onActionComplete({ evictedIds: [conversation.conversation_id] });
    } catch (error) {
      qlog.error('Failed to restore conversation:', error);
      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to restore conversation. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePermanentDelete = async () => {
    setIsProcessing(true);
    try {
      const result = await qapi.conversations.permanentDeleteById(conversation.conversation_id);
      if (result.error) throw new Error(result.error.statusText || 'Failed to delete');
      setShowPermanentDeleteModal(false);
      globalThis.Qwoted.showToast({
        toastTitle: 'Deleted',
        toastBody: 'Conversation permanently deleted',
        variant: 'success',
      });
      onActionComplete({ evictedIds: [conversation.conversation_id] });
    } catch (error) {
      qlog.error('Failed to permanently delete conversation:', error);
      globalThis.Qwoted.showToast({
        toastTitle: 'Error',
        toastBody: 'Failed to delete conversation. Please try again.',
        variant: 'danger',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleItemClick = () => {
    // Trashed conversations are not part of bulk selection (restore/delete use row actions instead).
    if (selectionMode && !isTrashed) {
      onToggleSelection(conversation.conversation_id);
      return;
    }

    handleClick(conversation.conversation_id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-conversation-id={conversation.conversation_id}
      className={classNames(
        'list-group-item',
        { 'list-group-item-action': !selected },
        'd-flex',
        'p-0',
        'border-0',
        'rounded-0',
        'border-bottom',
        'position-relative',
        'cursor-pointer',
        { 'bg-accent-blue-20': selected },
      )}
      onClick={handleItemClick}
      onKeyDown={(e) => {
        // Only act on keys aimed at the row itself. The row's own "…" menu is a
        // nested button, and swallowing its Space/Enter here would stop it opening.
        if (e.target !== e.currentTarget) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleItemClick();
        }
      }}
    >
      {/* Blue dot indicator for unread */}
      {unread && !selectionMode && (
        <div
          className="position-absolute bg-accent-blue-60 rounded-circle"
          style={{
            width: '12px',
            height: '12px',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        />
      )}

      {/* Checkbox in selection mode (not for trashed rows) */}
      {selectionMode && !isTrashed && (
        <div
          className="position-absolute"
          style={{
            left: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        >
          <input
            type="checkbox"
            className="form-check-input"
            checked={isSelected}
            readOnly
            style={{ cursor: 'pointer', width: 18, height: 18 }}
            tabIndex={-1}
          />
        </div>
      )}

      <div className="d-flex flex-column w-100" style={{ padding: '22px 32px' }}>
        {/* Row 1: Profile Image + Name + Date/Dropdown */}
        <div className="d-flex flex-row align-items-center w-100 mb-2">
          <div style={{ marginRight: 12 }}>
            <ConversationProfileImages profiles={displayProfiles} size={42} />
          </div>

          <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
            {/* Name + Dropdown */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                <strong className="paragraph-lg text-body text-truncate">{displayName}</strong>
                {/* Outside the Trash tab (i.e. mixed search results) a trashed row says so */}
                {isTrashed && (inSearchResults || activeMailbox !== 'trash') && (
                  <span className="badge rounded-pill border border-danger text-danger bg-transparent ms-2 align-middle fw-semibold">
                    Trash
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {!isTrashed && isMuted && (
                  <span
                    className="text-neutral-80 flex-shrink-0 lh-1 d-inline-flex align-items-center"
                    style={{ fontSize: '0.8125rem' }}
                    title="Muted"
                    role="img"
                    aria-label="Muted"
                  >
                    <i className="fa-solid fa-bell-slash" />
                  </span>
                )}

                {!selected && !isTrashed && (
                  <span className="paragraph-xs text-secondary text-nowrap">
                    {formatLastSentAt(conversation.last_sent_at)}
                  </span>
                )}

                {!selectionMode
                  && selected
                  && !isTrashed
                  && (dropdownOverride ?? (
                    <ConversationDropdown
                      conversation={conversation}
                      onActionComplete={onActionComplete}
                      small
                      activeMailbox={activeMailbox}
                    />
                  ))}

                {!selectionMode && isTrashed && (
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-icon btn-secondary text-neutral-80"
                      onClick={handleRestore}
                      disabled={isProcessing}
                      title="Restore"
                    >
                      <i className="fa-regular fa-arrow-rotate-left" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-icon btn-secondary text-neutral-80"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPermanentDeleteModal(true);
                      }}
                      disabled={isProcessing}
                      title="Delete permanently"
                    >
                      <i className="fa-regular fa-trash" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {displayTitle && <p className="paragraph-sm text-secondary mb-0">{truncateText(displayTitle, 60)}</p>}
            {/* Badges sit on their own line under the title — never squeezed against the name */}
            {titleBadges && <div className="d-flex flex-wrap gap-2 mt-1">{titleBadges}</div>}
          </div>
        </div>

        {/* Row 2: Subject/request title (when conversation originated from a pitch) */}
        {isPitchOrigin(conversation.origin_type) && (
          <p className="d-flex align-items-center paragraph-md fw-semibold text-body mb-1">
            <i className="fa-solid fa-megaphone me-2 text-primary-60" />
            <span className="text-truncate">{conversation.subject}</span>
          </p>
        )}

        {/* Row 3: Message preview or search hit snippets */}
        {conversation.search_hits?.length > 0 ? (
          <div
            className="paragraph-md text-body mb-0 text-truncate"
            // Safe: content is server-generated ts_headline HTML with <mark> tags,
            // sanitized by DOMPurify to strip anything beyond simple formatting.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(conversation.search_hits.map((hit) => hit.content).join(' ... ')),
            }}
          />
        ) : (
          <p className="paragraph-md text-body mb-0">
            {stripHtmlTags(conversation.last_body || '').trim()
              ? truncateText(stripHtmlTags(conversation.last_body), 80)
              : conversation.num_attachments > 0 && '📎 Attachment'}
          </p>
        )}
      </div>

      {isTrashed && (
        <ConfirmationModal
          show={showPermanentDeleteModal}
          onHide={() => setShowPermanentDeleteModal(false)}
          onConfirm={handlePermanentDelete}
          title="Permanently Delete Conversation"
          message="Are you sure you want to permanently delete this conversation?"
          detail="This action cannot be undone. The conversation will be permanently removed."
          confirmLabel="Yes, Permanently Delete"
          confirmingLabel="Deleting..."
          variant="danger"
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}

Conversation.propTypes = {
  dropdownOverride: PropTypes.node,
  conversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    conversation_id: PropTypes.string.isRequired,
    conversation_senders: PropTypes.arrayOf(
      PropTypes.shape({
        slug: PropTypes.string,
        name: PropTypes.string,
        first_name: PropTypes.string,
      }),
    ).isRequired,
    conversation_receivers: PropTypes.arrayOf(
      PropTypes.shape({
        slug: PropTypes.string,
        name: PropTypes.string,
        first_name: PropTypes.string,
      }),
    ).isRequired,
    last_sender_slug: PropTypes.string,
    last_sent_at: PropTypes.string,
    last_body: PropTypes.string,
    num_attachments: PropTypes.number,
    subject: PropTypes.string,
    origin_type: PropTypes.string,
    origin_expert: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
      profile_image_url: PropTypes.string,
      self_represented: PropTypes.bool,
      self_represented_profile_id: PropTypes.number,
    }),
    origin_product: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
      profile_image_url: PropTypes.string,
    }),
    receipt_read_at: PropTypes.string,
    receipt_archived_at: PropTypes.string,
    muted_at: PropTypes.string,
    receipt_muted_at: PropTypes.string,
    search_hits: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string,
      }),
    ),
  }).isRequired,
  handleClick: PropTypes.func.isRequired,
  selectedConversation: PropTypes.shape({
    conversation_id: PropTypes.string,
  }),
  onActionComplete: PropTypes.func.isRequired,
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelection: PropTypes.func,
  activeMailbox: PropTypes.string,
  inSearchResults: PropTypes.bool,
  titleBadges: PropTypes.node,
};

export default Conversation;
